import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Link } from '@liminal/web-ui/primitives/Link';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { ErrorState } from '@liminal/web-ui/feedback/ErrorState';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { formatOrderDate } from '@liminal/utils/date';
import { isHttpError } from '@liminal/errors/http-errors';
import { RewardBudget_State } from '@/api/generated';
import type { RewardBudget } from '@/api/types';
import { getErrorMessage } from '@/errors/messages';
import { useChangeRewardBudgetState, useRewardBudget, useRewardMerchants } from '@/hooks/useRewards';
import { budgetState, merchantState } from '@/components/ui/statusHelpers';
import { budgetWindowText, resourceId, usdText } from '@/lib/rewards';
import { BudgetUsage } from '../components/BudgetUsage';

/** Partners are a handful; one page holds every one that draws from this budget. */
const MERCHANTS_PAGE = 200;

type StateChange = 'pause' | 'resume' | 'close';

/** The state changes open from each state. Closing is for good. */
const CHANGES: Record<RewardBudget_State, readonly StateChange[]> = {
  [RewardBudget_State.STATE_OPEN]: ['pause', 'close'],
  [RewardBudget_State.STATE_PAUSED]: ['resume', 'close'],
  [RewardBudget_State.STATE_CLOSED]: [],
};

// A budget that is not open refuses every choice at its partners, Earn as
// well as Pay with LIL (partner_service gateChoice).
const CHANGE_COPY: Record<StateChange, { label: string; title: string; detail: string }> = {
  pause: {
    label: 'Pause',
    title: 'Pause this budget',
    detail: 'Customers at its partners cannot earn or pay with LIL until it resumes. Payments already paying back finish.',
  },
  resume: { label: 'Resume', title: 'Resume this budget', detail: 'Customers at its partners can earn and pay with LIL again.' },
  close: {
    label: 'Close',
    title: 'Close this budget',
    detail: 'For good: customers at its partners cannot earn or pay with LIL. Payments already paying back finish.',
  },
};

/** What each state means for the partners drawing from the budget. */
const STATE_MEANING: Record<RewardBudget_State, string> = {
  [RewardBudget_State.STATE_OPEN]: 'Customers at its partners can earn LIL or pay with LIL.',
  [RewardBudget_State.STATE_PAUSED]: 'Paused: customers at its partners cannot earn or pay with LIL until it resumes.',
  [RewardBudget_State.STATE_CLOSED]: 'Closed for good: customers at its partners cannot earn or pay with LIL.',
};

export function BudgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const budgetId = id ?? '';
  const budget = useRewardBudget(budgetId);

  if (budget.isLoading) return <LoadingSpinner />;
  if (budget.isError || !budget.data) {
    return <ErrorState message={getErrorMessage(budget.error)} onRetry={() => void budget.refetch()} />;
  }
  return <BudgetDetail budgetId={budgetId} budget={budget.data} />;
}

function BudgetDetail({ budgetId, budget }: { readonly budgetId: string; readonly budget: RewardBudget }) {
  const navigate = useNavigate();
  const [change, setChange] = useState<StateChange | null>(null);
  const [changeError, setChangeError] = useState('');
  // One idempotency key per opening of the dialog, kept across a retry.
  const [changeRequestId, setChangeRequestId] = useState('');
  const changeState = useChangeRewardBudgetState();
  // Merchants filter by state only, so this budget's partners are picked from one page of all of them.
  const merchants = useRewardMerchants(MERCHANTS_PAGE, '');
  if (budget.state === undefined) throw new Error(`budget ${budget.name} has no state`);
  const state = budgetState(budget.state);
  const changes = CHANGES[budget.state];
  const partners = (merchants.data?.rewardMerchants ?? []).filter((m) => m.budget === budget.name);

  const openChange = (next: StateChange) => {
    setChangeRequestId(crypto.randomUUID());
    setChange(next);
  };
  const closeChange = () => {
    setChange(null);
    setChangeError('');
  };

  const confirmChange = async () => {
    if (!change) return;
    if (!budget.etag) throw new Error(`budget ${budget.name} has no etag`);
    try {
      await changeState.mutateAsync({ id: budgetId, change, etag: budget.etag, requestId: changeRequestId });
      closeChange();
    } catch (e) {
      setChangeError(
        isHttpError(e) && e.status === 409
          ? 'This budget was changed by someone else. Cancel, reload and try again.'
          : getErrorMessage(e),
      );
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Button variant="link" size="sm" startIcon="arrow-back" onClick={() => navigate('/rewards/budgets')}>
          Budgets
        </Button>
      </Box>

      <PageHeader
        title={budget.displayName ?? budgetId}
        autoTitleCase={false}
        subtitle={STATE_MEANING[budget.state]}
        actions={
          <Stack direction="row" spacing={1}>
            {changes.map((c) => (
              <Button key={c} variant={c === 'close' ? 'danger' : 'secondary'} onClick={() => openChange(c)}>
                {CHANGE_COPY[c].label}
              </Button>
            ))}
            {budget.state !== RewardBudget_State.STATE_CLOSED && (
              <Button startIcon="pencil" onClick={() => navigate(`/rewards/budgets/${budgetId}/edit`)}>
                Edit
              </Button>
            )}
          </Stack>
        }
      />

      <Card variant="elevated">
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6">How much is left</Typography>
            <StatusPill tone={state.tone} label={state.label} size="sm" />
          </Stack>
          <BudgetUsage limitUsd={budget.limitUsd} spentUsd={budget.spentUsd} reservedUsd={budget.reservedUsd} detail="full" />
        </Stack>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
        <Card variant="elevated">
          <Stack spacing={2}>
            <Typography variant="h6">Rules</Typography>
            <DetailRow layout="stacked" label="Total it may pay back" value={usdText(budget.limitUsd)} />
            <DetailRow
              layout="stacked"
              label="Cap per payment"
              value={`${usdText(budget.maxPaymentUsd)}. A bigger payment cannot be paid with LIL.`}
            />
            <DetailRow layout="stacked" label="Runs" value={budgetWindowText(budget.startTime, budget.endTime)} />
            <DetailRow layout="stacked" label="Id" value={<CopyableId value={budgetId} />} />
          </Stack>
        </Card>

        <Card variant="elevated">
          <Stack spacing={2}>
            <Typography variant="h6">Partners on this budget</Typography>
            {merchants.isLoading ? (
              <Skeleton height={80} />
            ) : merchants.isError ? (
              <Typography variant="body2" color="error">
                {`The partners could not be read: ${getErrorMessage(merchants.error)}`}
              </Typography>
            ) : partners.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No partner draws from this budget yet.
              </Typography>
            ) : (
              <Stack>
                {partners.map((m) => {
                  const partnerState = merchantState(m.state);
                  return (
                    <Stack
                      key={m.name}
                      direction="row"
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ py: 1, borderTop: `1px solid ${webColors.border.light}` }}
                    >
                      <Typography variant="body2">
                        <Link component={RouterLink} to={`/rewards/partners/${resourceId(m.name, 'rewardMerchants')}`}>
                          {m.displayName ?? m.name}
                        </Link>
                      </Typography>
                      <StatusPill tone={partnerState.tone} label={partnerState.label} size="sm" />
                    </Stack>
                  );
                })}
              </Stack>
            )}
            {merchants.data?.nextPageToken && (
              <Typography variant="caption" color="text.secondary">
                {`Only the first ${MERCHANTS_PAGE} partners were checked.`}
              </Typography>
            )}
          </Stack>
        </Card>
      </Box>

      <Typography variant="caption" color="text.secondary">
        {[
          budget.createTime ? `Created ${formatOrderDate(budget.createTime)}` : null,
          budget.updateTime ? `last changed ${formatOrderDate(budget.updateTime)}` : null,
        ]
          .filter(Boolean)
          .join(', ')}
      </Typography>

      <ConfirmationDialog
        open={change !== null}
        title={change ? CHANGE_COPY[change].title : ''}
        confirmLabel={change ? CHANGE_COPY[change].label : ''}
        destructive={change === 'close'}
        loading={changeState.isPending}
        onConfirm={() => void confirmChange()}
        onCancel={closeChange}
      >
        <Stack spacing={2}>
          {change ? CHANGE_COPY[change].detail : ''}
          <FormErrorBanner error={changeError} />
        </Stack>
      </ConfirmationDialog>
    </Stack>
  );
}
